import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Check if running on native mobile platform
 */
export const isNativeMobile = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Setup push notifications for native iOS
 */
export const setupPushNotifications = async (): Promise<void> => {
  if (!isNativeMobile()) return;

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive === 'granted') {
      // Register with APNs
      await PushNotifications.register();
    } else {
      console.log('Push notification permission denied');
    }

    // Listen for registration token
    PushNotifications.addListener('registration', async (token) => {
      console.log('Push registration success, token:', token.value);
      
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const deviceFingerprintModule = await import('./device-fingerprint');
          const deviceId = await deviceFingerprintModule.generateDeviceFingerprint();
          
          // Store or update push token in database
          const { error } = await supabase
            .from('push_tokens')
            .upsert({
              user_id: user.id,
              token: token.value,
              platform: 'ios',
              device_fingerprint: deviceId,
              updated_at: new Date().toISOString(),
              last_used_at: new Date().toISOString()
            }, {
              onConflict: 'token'
            });
          
          if (error) {
            console.error('Error storing push token:', error);
          } else {
            console.log('Push token stored successfully');
          }
        }
      } catch (error) {
        console.error('Error in push token registration:', error);
      }
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Error on registration:', error);
    });

    // Handle incoming notifications when app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      // Show in-app notification or update ping count
      // Could trigger a ping list refresh here
    });

    // Handle notification taps (when user taps notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action performed:', action);
      // Navigate to dashboard or specific ping
      const data = action.notification.data;
      if (data.pingId) {
        // Navigate to specific ping
        window.location.href = `/dashboard?ping=${data.pingId}`;
      } else {
        // Navigate to dashboard
        window.location.href = '/dashboard';
      }
    });
  } catch (error) {
    console.error('Error setting up push notifications:', error);
  }
};

/**
 * Trigger haptic feedback
 */
export const triggerHaptic = async (
  style: 'light' | 'medium' | 'heavy' = 'light'
): Promise<void> => {
  if (!isNativeMobile()) return;

  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    }[style];
    
    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('Error triggering haptic:', error);
  }
};

/**
 * Configure iOS status bar
 */
export const configureStatusBar = async (): Promise<void> => {
  if (!isNativeMobile()) return;

  try {
    // Set light style (white text) to match purple background
    await StatusBar.setStyle({ style: Style.Light });
    
    // Set purple background color
    await StatusBar.setBackgroundColor({ color: '#8B5CF6' });
  } catch (error) {
    console.error('Error configuring status bar:', error);
  }
};

/**
 * Get current push notification permission status
 */
export const getPushPermissionStatus = async (): Promise<'granted' | 'denied' | 'prompt'> => {
  if (!isNativeMobile()) return 'denied';

  try {
    const permStatus = await PushNotifications.checkPermissions();
    const status = permStatus.receive;
    // Normalize status to match our expected types
    if (status === 'granted' || status === 'denied' || status === 'prompt') {
      return status;
    }
    // Handle 'prompt-with-rationale' as 'prompt'
    return 'prompt';
  } catch (error) {
    console.error('Error checking push permissions:', error);
    return 'denied';
  }
};
